import React, { useMemo } from 'react';
import { Skeleton } from 'antd';
import Task from '../../../../types/Task';
import { TaskActivity } from '../../../../types/TaskActivity';
import { WorkspaceAccessResponse } from '../../../../api/workspace';
import { User } from '../../../../types/User';
import { useGetTaskActivity } from '../../../../serverState/tasks';
import { ActivityLogEntry } from './ActivityLogEntry';

interface ActivityLogProps {
  task: Task;
  usersWithAccessToSpace: WorkspaceAccessResponse[];
}

/**
 * Build the synthetic "created the item" entry from the task itself. No
 * historical backfill exists, so this is derived client-side and rendered as
 * the oldest row — unless a real persisted `task_created` row already exists.
 */
const buildCreatedEntry = (task: Task, users: WorkspaceAccessResponse[]): TaskActivity => {
  // Match the "Created by" panel field (TaskView): task.createdBy keys off the
  // workspace-access row id (not the nested userId, which can be null). The
  // previous userId/Number() lookup failed to resolve the creator and fell back
  // to the placeholder avatar. Read the user fields from either the nested
  // `User` (server access payload) or the flat record, whichever carries them.
  const rec = users.find((u) => u.id === task.createdBy) as (WorkspaceAccessResponse & Partial<User>) | undefined;
  // Take each field from the nested User if present, else the flat record.
  const nested = rec?.User;
  return {
    id: -1, // synthetic
    taskId: task.id,
    type: 'task_created',
    field: null,
    oldValue: null,
    newValue: null,
    metadata: null,
    createdAt: new Date(task.createdAt).toISOString(),
    user: {
      id: Number(task.createdBy),
      fullName: nested?.fullName ?? rec?.fullName ?? null,
      email: nested?.email ?? rec?.email ?? '',
      profilePicturePath: nested?.profilePicturePath ?? rec?.profilePicturePath ?? null,
    },
  };
};

export const ActivityLog: React.FC<ActivityLogProps> = ({ task, usersWithAccessToSpace }) => {
  const { data, isLoading } = useGetTaskActivity(task.id);

  // Server returns newest-first; append the synthetic "created" row last unless
  // a real task_created row is already present (dedupe by type).
  const entries = useMemo(() => {
    const items = data?.items ?? [];
    const hasRealCreated = items.some((a) => a.type === 'task_created');
    return hasRealCreated ? items : [...items, buildCreatedEntry(task, usersWithAccessToSpace)];
  }, [data?.items, task, usersWithAccessToSpace]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 pt-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} active title={false} paragraph={{ rows: 1, width: ['60%'] }} />
        ))}
      </div>
    );
  }

  if (!entries.length) {
    return <div className="text-neutral-700 text-sm pt-2">No activity yet.</div>;
  }

  return (
    <div className="flex flex-col pt-1">
      {entries.map((activity, i) => (
        <React.Fragment key={activity.id}>
          {/* Timeline connector: an up-arrow centered under the 16px avatar column. */}
          {i > 0 && (
            <svg
              className="ml-1 my-0.5 text-neutral-700"
              width="8"
              height="14"
              viewBox="0 0 8 14"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 13V2M1.5 4.5 4 2 6.5 4.5"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          <ActivityLogEntry activity={activity} />
        </React.Fragment>
      ))}
    </div>
  );
};
